import * as _ from 'lodash';
import { IWatrRequest } from '@nkr/models/index';
import { Alert, Button, Glyphicon } from 'react-bootstrap';
import * as React from 'react';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { FileDocumentType } from '@nkr/enums/document-type';
import Drawer from 'rc-drawer';
import ClaimHistory from './claim-history';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import { AuthActionCreators, IApplicationState } from '@nkr/store/index';
import { IWatrViewerDocument } from '../utils/watr-viewer-document';
import { buildWatrSidebarFindHints, resolveSidebarDocType } from './document-viewer/locate/build-watr-find-hints';
import {
    closeDocumentViewerPopout,
    isDocumentViewerPopoutOpen,
    measureElementScreenBounds,
    syncDocumentViewerPopout
} from './document-viewer/document-viewer-popout';
import type { DocumentViewerPopoutState } from './document-viewer/document-viewer-popout';
import type { DocumentRef } from './document-viewer/types';
import { ExpenseType, MILEAGE_REVIEW_KEY } from './verify/constants/expense-types';
import { MileageReviewPanel } from './verify/components/MileageReviewPanel';
import {
    WatrDrawerIconBtn,
    WatrDrawerTools
} from '../styles';
import './document-viewer/text-layer.css';
import './document-viewer/viewer.css';

const DocumentViewerLazy = React.lazy(() => import('./document-viewer/index'));

const isMileageReviewFile = (fileId?: number | null) => fileId === MILEAGE_REVIEW_KEY;

const isPagedFileSidebar = (displayType: FileDocumentType, fileIds?: number[]) =>
    (displayType === FileDocumentType.Receipts || displayType === FileDocumentType.PayStub)
    && !!(fileIds && fileIds.length > 0);

const normalizeExpenseType = (expenseType: number): number =>
    expenseType === ExpenseType.CAR_SERVICE ? ExpenseType.TRANSPORTATION : expenseType;

const expenseTypeForReceiptFile = (
    fileId: number,
    fileIdToLineItemsMap: Map<number, any[]>
): number | null => {
    if (isMileageReviewFile(fileId)) {
        return ExpenseType.MILEAGE;
    }

    const lineItems = fileIdToLineItemsMap.get(fileId) || [];

    if (lineItems.length === 0) {
        return null;
    }

    return lineItems[0].expenseType;
};

const filesMatchingExpenseType = (
    expenseType: number,
    fileIdToLineItemsMap: Map<number, any[]>
): number[] => {
    if (expenseType === ExpenseType.MILEAGE && fileIdToLineItemsMap.has(MILEAGE_REVIEW_KEY)) {
        return [MILEAGE_REVIEW_KEY];
    }

    return Array.from(fileIdToLineItemsMap.entries())
        .filter(([, items]) => items.some(li => {
            if (expenseType === ExpenseType.TRANSPORTATION || expenseType === ExpenseType.CAR_SERVICE) {
                return li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE;
            }

            return li.expenseType === expenseType;
        }))
        .map(([fileId]) => fileId);
};

interface IProps {
    centerId: number;
    data: IWatrRequest | null;
    show: boolean;
    displayType: FileDocumentType,
    fileUrl: string,
    totalFiles?: number;
    fileIds?: number[];
    currentFileIndex?: number;
    fetchFileUrl?: (docType: FileDocumentType, lookupId: number) => Promise<string>;
    fileIdToLineItemsMap?: Map<number, any[]>;
    onApproveLineItems?: (lineItemIds: number[]) => Promise<void>;
    onRequestReviewLineItems?: (lineItemIds: number[]) => Promise<void>;
    onCancelView: () => void;
    getContainer?: () => any;
    inline?: boolean;
    onExpenseTypeChange?: (expenseType: number | null, fileIndex?: number) => void;
    onExpenseTypeApproved?: (expenseType: number) => void;
    onRefreshLineItems?: () => Promise<void>;
    onReviewRequiredStateChange?: (lineItemIds: number[], reviewRequired: boolean) => void;
    viewerDocs?: IWatrViewerDocument[];
    onViewerDocChange?: (docType: number, index: number) => void;
    uploadOrientedFile?: (docType: number, entityId: number, formData: FormData) => Promise<any>;
}

const AdditionalInfoSidebar = (props: IProps) => {
    const requestId = props.data != null ? props.data.id : undefined;
    const [labelName, setLabelName] = useState('Tax Document');
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [fileUrls, setFileUrls] = useState<Map<number, string>>(new Map());
    const [loadingFile, setLoadingFile] = useState<boolean>(false);
    const [currentFileUrl, setCurrentFileUrl] = useState<string>('');
    const [approvedExpenseTypes, setApprovedExpenseTypes] = useState<Set<number>>(new Set());
    const [lineItemStatus, setLineItemStatus] = useState<Map<number, 'approved' | 'review'>>(new Map());
    const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
    const keepPopoutOnCloseRef = useRef(false);

    useEffect(() => {
        if (props.viewerDocs && props.viewerDocs.length > 0) {
            const idx = Math.max(0, Math.min(currentIndex, props.viewerDocs.length - 1));
            const doc = props.viewerDocs[idx];
            const docLabel = (doc && doc.label) || '';
            const looksLikeFileName = /\.(pdf|png|jpe?g)$/i.test(docLabel);

            if (doc && docLabel && !looksLikeFileName) {
                setLabelName(docLabel);
                return;
            }
        }

        let label = '';

        switch (props.displayType) {
            case FileDocumentType.Tax: label = 'Tax Document'; break;
            case FileDocumentType.SsnVerification: label = 'SS# Verification'; break;
            case FileDocumentType.DsSelfEmployedAffidavit: label = 'Self Employment Affidavit'; break;
            case FileDocumentType.Receipts: label = 'Receipts'; break;
            case FileDocumentType.DonorShieldClaimHistory: label = 'Claim History'; break;
            case FileDocumentType.PayStub: label = 'Pay Stub'; break;
            default: label = '';
        }

        setLabelName(label);
    }, [props.displayType, props.viewerDocs, currentIndex]);

    useEffect(() => {
        if (!props.inline || !props.show) {
            return;
        }

        document.documentElement.classList.add('watr-viewer-open');

        return () => {
            document.documentElement.classList.remove('watr-viewer-open');
        };
    }, [props.inline, props.show]);

    useEffect(() => {
        setLineItemStatus(new Map());
        setApprovedExpenseTypes(new Set());
    }, [requestId]);

    const markLineItemStatus = (ids: number[], status: 'approved' | 'review') => {
        setLineItemStatus(prev => {
            const next = new Map(prev);

            ids.forEach(id => {
                next.set(id, status);
            });

            return next;
        });
    };

    // Don't reset approvedExpenseTypes when fileIds change - approvals should persist when navigating between files

    const [previousFileIdsKey, setPreviousFileIdsKey] = useState<string>('');
    const [previousViewerDocsKey, setPreviousViewerDocsKey] = useState<string>('');
    const [previousViewerPropIndex, setPreviousViewerPropIndex] = useState<number | undefined>(undefined);
    const [viewerDocUrls, setViewerDocUrls] = useState<Map<string, string>>(new Map());

    const viewerDocKey = (doc: IWatrViewerDocument) => `${doc.docType}:${doc.lookupId}`;

    useEffect(() => {
        if (props.viewerDocs && props.viewerDocs.length > 0) {
            const docsKey = props.viewerDocs.map(d => viewerDocKey(d)).join('|');
            const docsChanged = docsKey !== previousViewerDocsKey;
            const propIndex = props.currentFileIndex;
            const propIndexChanged = propIndex !== previousViewerPropIndex;

            let validIndex = currentIndex;
            if (docsChanged) {
                setPreviousViewerDocsKey(docsKey);
                setViewerDocUrls(new Map());
                validIndex = Math.max(0, Math.min(propIndex ?? 0, props.viewerDocs.length - 1));
                setCurrentIndex(validIndex);
                setPreviousViewerPropIndex(propIndex);
            } else if (propIndexChanged && propIndex !== undefined) {
                validIndex = Math.max(0, Math.min(propIndex, props.viewerDocs.length - 1));
                setCurrentIndex(validIndex);
                setPreviousViewerPropIndex(propIndex);
            } else {
                validIndex = Math.max(0, Math.min(currentIndex, props.viewerDocs.length - 1));
            }

            const targetDoc = props.viewerDocs[validIndex];
            if (!targetDoc) {
                setCurrentFileUrl('');
                return;
            }

            const key = viewerDocKey(targetDoc);
            if (viewerDocUrls.has(key)) {
                setCurrentFileUrl(viewerDocUrls.get(key)!);
                props.onViewerDocChange?.(targetDoc.docType, validIndex);
                return;
            }

            if (props.fileUrl && (docsChanged || propIndexChanged)) {
                setCurrentFileUrl(props.fileUrl);
                setViewerDocUrls(prev => {
                    const next = new Map(prev);
                    next.set(key, props.fileUrl);
                    return next;
                });
                props.onViewerDocChange?.(targetDoc.docType, validIndex);
                return;
            }

            if (props.fetchFileUrl) {
                setLoadingFile(true);
                props.fetchFileUrl(targetDoc.docType, targetDoc.lookupId)
                    .then(url => {
                        if (url) {
                            setCurrentFileUrl(url);
                            setViewerDocUrls(prev => {
                                const next = new Map(prev);
                                next.set(key, url);
                                return next;
                            });
                            props.onViewerDocChange?.(targetDoc.docType, validIndex);
                        }
                    })
                    .catch(err => {
                        console.error('Error fetching viewer doc URL:', err);
                    })
                    .finally(() => {
                        setLoadingFile(false);
                    });
            }
            return;
        }

        if (isPagedFileSidebar(props.displayType, props.fileIds)) {
            const currentFileIdsKey = props.fileIds.join(',');
            const fileIdsChanged = currentFileIdsKey !== previousFileIdsKey;
            const preferredIndex = props.currentFileIndex !== undefined ? props.currentFileIndex : 0;
            const validPreferred = Math.max(0, Math.min(preferredIndex, props.fileIds.length - 1));
            
            if (fileIdsChanged) {
                setCurrentIndex(validPreferred);
                setPreviousFileIdsKey(currentFileIdsKey);
                setFileUrls(prev => {
                    const filteredMap = new Map<number, string>();
                    props.fileIds!.forEach(id => {
                        if (prev.has(id) && !isMileageReviewFile(id)) {
                            filteredMap.set(id, prev.get(id)!);
                        }
                    });
                    return filteredMap;
                });
            }
            
            const targetIndex = fileIdsChanged
                ? validPreferred
                : (props.currentFileIndex !== undefined ? props.currentFileIndex : currentIndex);
            const validIndex = Math.max(0, Math.min(targetIndex, props.fileIds.length - 1));
            const targetFileId = props.fileIds[validIndex];
            
            if (!fileIdsChanged && props.currentFileIndex !== undefined && props.currentFileIndex !== currentIndex) {
                setCurrentIndex(validIndex);
            }

            if (isMileageReviewFile(targetFileId)) {
                setCurrentFileUrl('');
                return;
            }
            
            if (targetFileId) {
                setFileUrls(prev => {
                    const filteredMap = new Map<number, string>();
                    props.fileIds!.forEach(id => {
                        if (prev.has(id) && !isMileageReviewFile(id)) {
                            filteredMap.set(id, prev.get(id)!);
                        }
                    });
                    
                    if (filteredMap.has(targetFileId)) {
                        setCurrentFileUrl(filteredMap.get(targetFileId)!);
                        return filteredMap;
                    }
                    
                    if (props.fileUrl && validIndex === preferredIndex && fileIdsChanged) {
                        setCurrentFileUrl(props.fileUrl);
                        filteredMap.set(targetFileId, props.fileUrl);
                        return filteredMap;
                    }
                    
                    if (props.fetchFileUrl && !filteredMap.has(targetFileId)) {
                        setLoadingFile(true);
                        props.fetchFileUrl(props.displayType, targetFileId)
                            .then(url => {
                                if (url) {
                                    setCurrentFileUrl(url);
                                    setFileUrls(currentMap => {
                                        const updatedMap = new Map(currentMap);
                                        props.fileIds!.forEach(id => {
                                            if (currentMap.has(id) && !isMileageReviewFile(id)) {
                                                updatedMap.set(id, currentMap.get(id)!);
                                            }
                                        });
                                        updatedMap.set(targetFileId, url);
                                        return updatedMap;
                                    });
                                }
                            })
                            .catch(err => {
                                console.error('Error fetching file URL:', err);
                            })
                            .finally(() => {
                                setLoadingFile(false);
                            });
                    }
                    
                    return filteredMap;
                });
            } else {
                setCurrentFileUrl('');
            }
        } else {
            if (props.currentFileIndex !== undefined) {
                setCurrentIndex(props.currentFileIndex);
            }
            setCurrentFileUrl(props.fileUrl);
        }
    }, [props.displayType, props.fileUrl, props.fileIds, props.currentFileIndex, props.fetchFileUrl, previousFileIdsKey, props.viewerDocs, previousViewerDocsKey, previousViewerPropIndex]);

    // Navigation handlers
    const loadViewerDocAtIndex = async (newIndex: number) => {
        if (!props.viewerDocs || newIndex < 0 || newIndex >= props.viewerDocs.length) {
            return;
        }

        const doc = props.viewerDocs[newIndex];
        setCurrentIndex(newIndex);
        setLabelName(doc.label);
        props.onViewerDocChange?.(doc.docType, newIndex);

        if (props.fileIds && props.fileIds[newIndex] != null) {
            notifyExpenseTypeForFile(props.fileIds[newIndex]);
        }

        const key = viewerDocKey(doc);
        if (viewerDocUrls.has(key)) {
            setCurrentFileUrl(viewerDocUrls.get(key)!);
            return;
        }

        if (!props.fetchFileUrl) {
            return;
        }

        setLoadingFile(true);
        try {
            const url = await props.fetchFileUrl(doc.docType, doc.lookupId);
            if (url) {
                setCurrentFileUrl(url);
                setViewerDocUrls(prev => {
                    const next = new Map(prev);
                    next.set(key, url);
                    return next;
                });
            }
        } catch (err) {
            console.error('Error fetching viewer doc URL:', err);
        } finally {
            setLoadingFile(false);
        }
    };

    const notifyExpenseTypeForFile = (fileId: number) => {
        if (!props.fileIdToLineItemsMap || !props.onExpenseTypeChange) {
            return;
        }

        const expenseType = expenseTypeForReceiptFile(fileId, props.fileIdToLineItemsMap);

        if (expenseType == null) {
            return;
        }

        const filesForType = filesMatchingExpenseType(expenseType, props.fileIdToLineItemsMap);
        const expenseTypeFileIndex = filesForType.indexOf(fileId);

        if (expenseTypeFileIndex < 0) {
            return;
        }

        try {
            props.onExpenseTypeChange(expenseType, expenseTypeFileIndex);
        } catch (err) {
            console.error('Error updating expense type on navigation:', err);
        }
    };

    const goToReceiptFile = async (newIndex: number) => {
        if (!props.fileIds || newIndex < 0 || newIndex >= props.fileIds.length) {
            return;
        }

        const fileId = props.fileIds[newIndex];

        setCurrentIndex(newIndex);
        notifyExpenseTypeForFile(fileId);

        if (props.viewerDocs && props.viewerDocs[newIndex]) {
            setLabelName(props.viewerDocs[newIndex].label);
            props.onViewerDocChange?.(props.viewerDocs[newIndex].docType, newIndex);
        } else {
            props.onViewerDocChange?.(props.displayType, newIndex);
        }

        if (isMileageReviewFile(fileId)) {
            setCurrentFileUrl('');
            return;
        }

        if (fileUrls.has(fileId)) {
            setCurrentFileUrl(fileUrls.get(fileId)!);
            return;
        }

        if (!props.fetchFileUrl) {
            return;
        }

        setLoadingFile(true);

        try {
            const url = await props.fetchFileUrl(props.displayType, fileId);

            if (url) {
                setCurrentFileUrl(url);
                setFileUrls(prev => {
                    const newMap = new Map(prev);
                    props.fileIds!.forEach(id => {
                        if (prev.has(id)) {
                            newMap.set(id, prev.get(id)!);
                        }
                    });
                    newMap.set(fileId, url);
                    return newMap;
                });
            }
        } catch (err) {
            console.error('Error fetching file URL:', err, { fileId });
        } finally {
            setLoadingFile(false);
        }
    };

    const goToPagedIndex = async (newIndex: number) => {
        const pagedTotal = (props.viewerDocs && props.viewerDocs.length > 0)
            ? props.viewerDocs.length
            : (props.fileIds && props.fileIds.length > 0 ? props.fileIds.length : 0);

        if (newIndex < 0 || newIndex >= pagedTotal) {
            return;
        }

        if (props.viewerDocs && props.viewerDocs.length > 0) {
            await loadViewerDocAtIndex(newIndex);
            return;
        }

        await goToReceiptFile(newIndex);
    };

    const handlePrevious = async () => {
        await goToPagedIndex(currentIndex - 1);
    };

    const handleNext = async () => {
        await goToPagedIndex(currentIndex + 1);
    };

    const handleApproveCurrentReceipt = async () => {
        if (!props.onApproveLineItems || !props.fileIds || !props.fileIdToLineItemsMap) {
            return;
        }

        const currentFileId = props.fileIds[currentIndex];
        const lineItems = props.fileIdToLineItemsMap.get(currentFileId) || [];

        if (lineItems.length === 0) {
            return;
        }

        const primaryItem = lineItems[0];
        const lineItemIds = lineItems.map(item => item.id);

        try {
            await props.onApproveLineItems(lineItemIds);
            markLineItemStatus(lineItemIds, 'approved');

            if (primaryItem.expenseType === undefined || primaryItem.expenseType === null) {
                return;
            }

            if (props.onReviewRequiredStateChange) {
                props.onReviewRequiredStateChange(lineItemIds, false);
            }

            const totalFiles = props.fileIds.length > 0 ? props.fileIds.length : (props.totalFiles || 1);
            const hasNextFile = currentIndex < totalFiles - 1;
            let isLeavingType = !hasNextFile;

            if (hasNextFile) {
                const nextFileId = props.fileIds[currentIndex + 1];
                const nextType = expenseTypeForReceiptFile(nextFileId, props.fileIdToLineItemsMap);

                if (nextType == null) {
                    isLeavingType = true;
                } else {
                    isLeavingType = normalizeExpenseType(primaryItem.expenseType) !== normalizeExpenseType(nextType);
                }
            }

            if (isLeavingType && props.displayType !== FileDocumentType.PayStub) {
                setApprovedExpenseTypes(prev => {
                    const next = new Set(prev);
                    next.add(primaryItem.expenseType);
                    return next;
                });

                if (props.onExpenseTypeApproved) {
                    await props.onExpenseTypeApproved(primaryItem.expenseType);
                }
            }

            if (hasNextFile) {
                await goToPagedIndex(currentIndex + 1);
            }
        } catch (err) {
            console.error('Error approving line items:', err);
        }
    };

    const hasViewerDocs = !!(props.viewerDocs && props.viewerDocs.length > 0);
    const totalFiles = hasViewerDocs
        ? props.viewerDocs!.length
        : (props.fileIds && props.fileIds.length > 0 ? props.fileIds.length : (props.totalFiles || 1));
    const canGoPrevious = totalFiles > 1 && currentIndex > 0;
    const canGoNext = totalFiles > 1 && currentIndex < totalFiles - 1;
    const pagerLabel = props.displayType === FileDocumentType.Receipts
        ? 'Expense'
        : (props.displayType === FileDocumentType.PayStub ? 'Pay Stub' : 'File');
    const currentFileId = props.fileIds && props.fileIds.length > 0
        ? props.fileIds[currentIndex]
        : undefined;
    const isMileageReview = isMileageReviewFile(currentFileId);
    const currentLineItems = (props.fileIdToLineItemsMap && currentFileId != null)
        ? (props.fileIdToLineItemsMap.get(currentFileId) || [])
        : [];
    const showPagedDecision = isPagedFileSidebar(props.displayType, props.fileIds) && currentLineItems.length > 0;
    const primaryDecisionItem = currentLineItems[0];
    const decisionAmount = showPagedDecision
        ? (props.displayType === FileDocumentType.PayStub
            ? (primaryDecisionItem.amount || 0)
            : currentLineItems.reduce((sum, item) => sum + (item.amount || 0), 0))
        : 0;
    const decisionDate = showPagedDecision && primaryDecisionItem && primaryDecisionItem.expenseDate
        ? moment(primaryDecisionItem.expenseDate).format('MMM D, YYYY')
        : '';
    const decisionDateLabel = props.displayType === FileDocumentType.PayStub ? 'Pay date' : 'Date';

    const activeDocType = resolveSidebarDocType(
        props.displayType,
        props.viewerDocs,
        currentIndex,
        props.fileIds
    );

    const currentReceiptLineItems = useMemo(() => {
        if (!isPagedFileSidebar(props.displayType, props.fileIds) || !props.fileIdToLineItemsMap) {
            return null;
        }
        const fileId = props.fileIds[currentIndex];
        if (fileId == null) {
            return null;
        }
        return props.fileIdToLineItemsMap.get(fileId) || [];
    }, [props.displayType, props.fileIds, props.fileIdToLineItemsMap, currentIndex]);

    const findHints = useMemo(() => {
        if (!props.data || props.displayType === FileDocumentType.DonorShieldClaimHistory) {
            return [];
        }
        return buildWatrSidebarFindHints(props.data, activeDocType, currentReceiptLineItems, currentIndex);
    }, [props.data, props.displayType, activeDocType, currentReceiptLineItems, currentIndex]);

    const documentRef: DocumentRef | undefined = useMemo(() => {
        if (!props.data || props.displayType === FileDocumentType.DonorShieldClaimHistory) {
            return undefined;
        }
        const fileId = props.fileIds && props.fileIds.length > 0 ? props.fileIds[currentIndex] : undefined;
        return {
            docType: activeDocType,
            lookupId: props.viewerDocs && props.viewerDocs[currentIndex]
                ? props.viewerDocs[currentIndex].lookupId
                : (fileId != null ? fileId : requestId),
            fileId,
            label: labelName,
            fileIndex: currentIndex,
            totalFiles
        };
    }, [props.data, props.displayType, props.viewerDocs, props.fileIds, activeDocType, currentIndex, labelName, totalFiles]);

    const orientedFileName = useMemo(() => {
        const base = (labelName || 'document').replace(/[^\w\-]+/g, '_');
        const path = (currentFileUrl || '').split('?')[0];
        const match = path.match(/\.(jpe?g|png)$/i);
        return `${base}${match ? match[0].toLowerCase() : '.jpg'}`;
    }, [labelName, currentFileUrl]);

    const handleOrientedSave = useCallback(async (blob: Blob, name: string) => {
        const lookupId = documentRef && documentRef.lookupId != null
            ? documentRef.lookupId
            : requestId;

        if (lookupId == null) {
            toast.error('Unable to save orientation');
            throw new Error('lookupId missing');
        }

        if (!props.uploadOrientedFile) {
            toast.error('Unable to save orientation');
            throw new Error('uploadOrientedFile missing');
        }
        const formData = new FormData();
        formData.append('file', blob, name || orientedFileName);
        const response = await props.uploadOrientedFile(activeDocType, lookupId, formData);
        if (!response || (response.status != null && response.status >= 400)) {
            toast.error('Could not save orientation');
            throw new Error('upload failed');
        }
        toast.success('Orientation saved');
        if (props.fetchFileUrl) {
            const url = await props.fetchFileUrl(activeDocType, lookupId);
            if (url) {
                setCurrentFileUrl(url);
                setFileUrls(prev => {
                    const next = new Map(prev);
                    if (props.fileIds && props.fileIds[currentIndex] != null) {
                        next.set(props.fileIds[currentIndex], url);
                    }
                    return next;
                });
            }
        }
    }, [
        documentRef,
        requestId,
        props.uploadOrientedFile,
        props.fetchFileUrl,
        props.fileIds,
        activeDocType,
        orientedFileName,
        currentIndex
    ]);

    const popOutState: DocumentViewerPopoutState | null = useMemo(() => {
        if (!currentFileUrl || props.displayType === FileDocumentType.DonorShieldClaimHistory) {
            return null;
        }
        return {
            url: currentFileUrl,
            documentRef,
            title: labelName,
            findHints,
            showLocate: true,
            allowOcr: true,
            allowRotate: true,
            autoRunOcr: true,
            fileName: orientedFileName,
            verifyLabel: labelName,
            autoSelectRequired: true,
            onOrientedSave: handleOrientedSave
        };
    }, [currentFileUrl, props.displayType, documentRef, labelName, findHints, orientedFileName, handleOrientedSave]);

    useEffect(() => {
        if (!props.show) {
            if (keepPopoutOnCloseRef.current) {
                keepPopoutOnCloseRef.current = false;
                return;
            }
            closeDocumentViewerPopout();
            return;
        }
        if (isDocumentViewerPopoutOpen() && popOutState) {
            syncDocumentViewerPopout(popOutState);
        }
    }, [props.show, popOutState]);

    const handlePopOut = () => {
        if (!popOutState || isMileageReviewFile(props.fileIds && props.fileIds[currentIndex])) {
            toast.error('No document is available to pop out.');
            return;
        }

        const slot = document.querySelector('.watr-document-viewer-slot') as HTMLElement | null;
        const bounds = slot ? measureElementScreenBounds(slot) : null;
        const opened = syncDocumentViewerPopout(popOutState, bounds);

        if (!opened) {
            return;
        }

        keepPopoutOnCloseRef.current = true;
        props.onCancelView();
    };

    const handleCancelView = () => {
        keepPopoutOnCloseRef.current = false;
        closeDocumentViewerPopout();
        props.onCancelView();
    };

    const handleRequestReviewCurrentReceipt = async () => {
        if (!props.onRequestReviewLineItems || !props.fileIds || !props.fileIdToLineItemsMap) {
            return;
        }

        const currentFileId = props.fileIds[currentIndex];
        const lineItems = props.fileIdToLineItemsMap.get(currentFileId) || [];
        const lineItemIds = lineItems.map(li => li.id);

        if (lineItemIds.length === 0) {
            return;
        }

        try {
            await props.onRequestReviewLineItems(lineItemIds);
            markLineItemStatus(lineItemIds, 'review');

            if (props.onReviewRequiredStateChange) {
                props.onReviewRequiredStateChange(lineItemIds, true);
            }

            const totalPaged = props.fileIds.length > 0
                ? props.fileIds.length
                : (props.totalFiles || 1);

            if (currentIndex < totalPaged - 1) {
                await goToPagedIndex(currentIndex + 1);
                return;
            }

            if (props.onExpenseTypeChange) {
                try {
                    props.onExpenseTypeChange(null);
                } catch (err) {
                    console.error('Error clearing expense type on request review:', err);
                }
            }

            handleCancelView();
        } catch (err) {
            console.error('Error requesting review:', err);
        }
    };

    return (
        <Drawer
            width={props.inline ? '100%' : '53vw'}
            placement='left'
            getContainer={props.inline ? false : props.getContainer}
            open={props.show}
            motion={{
                motionName: '',
                motionAppear: true,
                motionEnter: true,
                motionLeave: true,
            }}
            autoFocus={false}
            mask={false}
            styles={props.inline ? {
                wrapper: { height: '100%', overflow: 'hidden' },
                content: { height: '100%', overflow: 'hidden' }
            } : undefined}
            style={{
                zIndex: 1300,
                pointerEvents: 'auto',
                ...(props.inline
                    ? { position: 'relative', height: '100%', overflow: 'hidden' }
                    : {
                        transform: props.show ? 'translateX(0)' : 'translateX(-100%)',
                        transition: 'transform 7s ease-out'
                    })
            }}
        >
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 24,
                        flex: '0 0 auto',
                        backgroundColor: '#ffffff',
                        padding: '12px 16px',
                        zIndex: 2,
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        {/* Left: Receipts title and expense types */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: '0 1 auto', paddingRight: 8 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#212529' }}>{labelName}</h3>
                            
                            {isPagedFileSidebar(props.displayType, props.fileIds) && props.fileIdToLineItemsMap && (() => {
                                const currentFileId = props.fileIds[currentIndex];
                                const lineItems = props.fileIdToLineItemsMap.get(currentFileId) || [];
                                const expenseTypeNames: { [key: number]: string } = {
                                    1: 'Lodging',
                                    2: 'Flight',
                                    3: 'Transportation',
                                    4: 'Dependent Care',
                                    5: 'Meals',
                                    6: 'Other',
                                    7: 'Mileage'
                                };
                                
                                if (lineItems.length > 0) {
                                    const primaryItem = lineItems[0];
                                    const expenseType = isMileageReviewFile(currentFileId)
                                        ? ExpenseType.MILEAGE
                                        : primaryItem.expenseType;
                                    const expenseTypeName = props.displayType === FileDocumentType.PayStub
                                        ? 'Pay Stub'
                                        : (expenseTypeNames[expenseType] || 'Unknown');
                                    
                                    const itemStatuses = lineItems.map(li =>
                                        lineItemStatus.get(li.id)
                                        || (li.approved ? 'approved' : (li.reviewRequired === true ? 'review' : null))
                                    );
                                    const hasReviewRequired = props.displayType === FileDocumentType.PayStub
                                        ? itemStatuses.some(status => status === 'review')
                                        : lineItems.some(li => li.reviewRequired === true);
                                    const isApproved = props.displayType === FileDocumentType.PayStub
                                        ? itemStatuses.some(status => status === 'approved')
                                        : approvedExpenseTypes.has(expenseType);
                                    
                                    let status = 'Pending';
                                    let statusBgColor = '#e7f3ff';
                                    let statusBorderColor = '#007bff';
                                    let statusTextColor = '#004085';
                                    let statusBadgeBg = '#007bff';
                                    let statusBadgeText = 'white';
                                    
                                    if (isApproved) {
                                        status = 'Approved';
                                        statusBgColor = '#d4edda';
                                        statusBorderColor = '#28a745';
                                        statusTextColor = '#155724';
                                        statusBadgeBg = '#28a745';
                                        statusBadgeText = 'white';
                                    } else if (hasReviewRequired) {
                                        status = 'Pending Review';
                                        statusBgColor = '#fff3cd';
                                        statusBorderColor = '#ffc107';
                                        statusTextColor = '#856404';
                                        statusBadgeBg = '#ffc107';
                                        statusBadgeText = '#856404';
                                    }
                                    
                                    return (
                                        <>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                padding: '4px 12px',
                                                borderRadius: '6px',
                                                backgroundColor: statusBgColor,
                                                border: `2px solid ${statusBorderColor}`
                                            }}>
                                                {expenseTypeName !== labelName && (
                                                <span style={{ 
                                                    color: statusTextColor, 
                                                    fontSize: '14px', 
                                                    fontWeight: 600 
                                                }}>
                                                    {expenseTypeName}
                                                </span>
                                                )}
                                                <span style={{
                                                    backgroundColor: statusBadgeBg,
                                                    color: statusBadgeText,
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {status}
                                                </span>
                                            </div>
                                            {lineItems.length > 1 && (
                                                <span style={{ 
                                                    color: '#6c757d', 
                                                    fontSize: '12px',
                                                    fontWeight: 500
                                                }}>
                                                    {lineItems.length} items
                                                </span>
                                            )}
                                        </>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <div
                            ref={setToolbarHost}
                            className="dvToolbarSlot"
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
                            <WatrDrawerTools>
                                {props.displayType !== FileDocumentType.DonorShieldClaimHistory
                                    && currentFileUrl
                                    && !isMileageReviewFile(props.fileIds && props.fileIds[currentIndex]) && (
                                    <WatrDrawerIconBtn
                                        type="button"
                                        onClick={handlePopOut}
                                        disabled={loadingFile}
                                        title="Pop out"
                                        aria-label="Pop out"
                                    >
                                        <Glyphicon glyph="new-window" />
                                    </WatrDrawerIconBtn>
                                )}
                                <WatrDrawerIconBtn
                                    type="button"
                                    onClick={handleCancelView}
                                    title="Close"
                                    aria-label="Close"
                                >
                                    <Glyphicon glyph="remove" />
                                </WatrDrawerIconBtn>
                            </WatrDrawerTools>
                        </div>
                    </div>

                    <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {props.data != null && props.displayType != FileDocumentType.DonorShieldClaimHistory && (
                            <div style={{ flex: '1 1 auto', minHeight: 0, height: '100%', overflow: 'hidden' }}>
                                {loadingFile ? (
                                    <Alert bsStyle='info' bsSize='sm'><Glyphicon glyph='refresh' /> Loading file...</Alert>
                                ) : isMileageReview || currentFileUrl ? (
                                    <Suspense fallback={<Alert bsStyle='info' bsSize='sm'><Glyphicon glyph='refresh' /> Loading viewer...</Alert>}>
                                        <DocumentViewerLazy
                                            key={`${activeDocType}:${documentRef?.lookupId}:${currentIndex}:${isMileageReview ? 'mileage' : currentFileUrl}`}
                                            url={isMileageReview ? '' : currentFileUrl}
                                            documentRef={documentRef}
                                            height="100%"
                                            findHints={isMileageReview ? [] : findHints}
                                            showLocate={!isMileageReview}
                                            allowOcr={!isMileageReview}
                                            allowRotate={!isMileageReview}
                                            autoRunOcr={!isMileageReview}
                                            fileName={orientedFileName}
                                            onOrientedSave={isMileageReview ? undefined : handleOrientedSave}
                                            verifyLabel={labelName}
                                            autoSelectRequired={!isMileageReview}
                                            previewContent={isMileageReview ? (
                                                <MileageReviewPanel
                                                    data={props.data}
                                                    lineItems={currentLineItems as any[]}
                                                />
                                            ) : undefined}
                                            filePager={{
                                                label: pagerLabel,
                                                current: currentIndex + 1,
                                                total: Math.max(totalFiles, 1),
                                                canPrev: canGoPrevious,
                                                canNext: canGoNext,
                                                loading: loadingFile,
                                                onPrev: handlePrevious,
                                                onNext: handleNext
                                            }}
                                            toolbarHost={isMileageReview ? undefined : toolbarHost}
                                            fileActions={showPagedDecision ? (
                                                <div className="dvFileDecision">
                                                    <div className="dvFileDecisionSummary">
                                                        <div className="dvFileDecisionFact">
                                                            <span className="dvFileDecisionLabel">Amount</span>
                                                            <span className="dvFileDecisionValue">
                                                                ${decisionAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </div>
                                                        {decisionDate ? (
                                                            <div className="dvFileDecisionFact dvFileDecisionFactEnd">
                                                                <span className="dvFileDecisionLabel">{decisionDateLabel}</span>
                                                                <span className="dvFileDecisionValue dvFileDecisionDateValue">
                                                                    {decisionDate}
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div className="dvFileDecisionActions">
                                                        <Button
                                                            disabled={!props.onRequestReviewLineItems}
                                                            onClick={handleRequestReviewCurrentReceipt}
                                                        >
                                                            {props.displayType === FileDocumentType.PayStub ? 'Flag for Review' : 'Request Review'}
                                                        </Button>
                                                        <Button
                                                            bsStyle="success"
                                                            disabled={!props.onApproveLineItems}
                                                            onClick={handleApproveCurrentReceipt}
                                                        >
                                                            Approve
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : undefined}
                                        />
                                    </Suspense>
                                ) : (
                                    <Alert bsStyle='warning' bsSize='sm'><Glyphicon glyph='warning-sign' /> UNABLE TO DISPLAY FILE</Alert>
                                )}
                            </div>
                        )}

                        {props.data != null && props.displayType == FileDocumentType.DonorShieldClaimHistory && (
                            <div>
                                <ClaimHistory data={props.data} travels={props.data.requestTravels} />
                            </div>
                        )}
                    </div>

                <div className='text-center' style={{ flex: '0 0 auto', backgroundColor: '#d6f2d6' }}>
                    <Button bsStyle='link' block onClick={handleCancelView}>Close This View</Button>
                </div>
            </div>
        </Drawer>
    );
}

function mapDispatchToProps(dispatch: Dispatch<IApplicationState>) {
    return {
        uploadOrientedFile: (docType: number, entityId: number, formData: FormData) =>
            dispatch(AuthActionCreators.apiPostFormData({
                url: `${__API__}/file/upload/${docType}/${entityId}`,
                data: formData
            })) as any
    };
}

export default connect(null, mapDispatchToProps)(AdditionalInfoSidebar as any) as any;
